import { defineStore } from 'pinia';
import { authService, type LoginRequest, type TokenResponse } from '../services/auth';
import type { User } from '../types';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  error: string | null;
  sessionWatcherCleanup: (() => void) | null;
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    user: null,
    isLoading: false,
    error: null,
    sessionWatcherCleanup: null,
  }),

  getters: {
    isAuthenticated: (state) => !!state.user,
    permissions: (state) => state.user?.permissions || [],
    userType: (state) => state.user?.userType || null,
    tenantId: (state) => state.user?.tenantId || null,
    partnerId: (state) => state.user?.partnerId || null,
  },

  actions: {
    async login(credentials: LoginRequest, rememberMe: boolean = true): Promise<TokenResponse> {
      this.isLoading = true;
      this.error = null;

      try {
        const response = await authService.login(credentials);

        if (!response.mfa_required) {
          authService.setTokens(response, rememberMe);
          await this.fetchUser();
          // Start session expiry watcher
          this.startSessionWatcher();
        }

        return response;
      } catch (e: any) {
        this.error = e.response?.data?.detail || 'Login failed';
        throw e;
      } finally {
        this.isLoading = false;
      }
    },

    async verifyMfa(code: string, mfaToken: string, rememberMe: boolean = true): Promise<void> {
      this.isLoading = true;
      this.error = null;

      try {
        const response = await authService.verifyMfa(mfaToken, code);
        authService.setTokens(response, rememberMe);
        await this.fetchUser();
        // Start session expiry watcher
        this.startSessionWatcher();
      } catch (e: any) {
        this.error = e.response?.data?.detail || 'MFA verification failed';
        throw e;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Set tokens (generic method for MFA success, etc.)
     */
    setTokens(tokens: TokenResponse, rememberMe: boolean = true): void {
      authService.setTokens(tokens, rememberMe);
      // Start session expiry watcher
      this.startSessionWatcher();
    },

    /**
     * Set tokens after SSO login (alias for setTokens)
     */
    setTokensFromSSO(tokens: TokenResponse, rememberMe: boolean = true): void {
      this.setTokens(tokens, rememberMe);
    },

    async logout(): Promise<void> {
      try {
        // Stop session watcher
        this.stopSessionWatcher();
        await authService.logout();
      } finally {
        this.user = null;
        localStorage.removeItem('user');
        sessionStorage.removeItem('user');
      }
    },

    /**
     * Start session expiry watcher
     */
    startSessionWatcher(): void {
      // Stop any existing watcher first
      this.stopSessionWatcher();
      // Start new watcher
      this.sessionWatcherCleanup = authService.startSessionExpiryWatcher();
    },

    /**
     * Stop session expiry watcher
     */
    stopSessionWatcher(): void {
      if (this.sessionWatcherCleanup) {
        this.sessionWatcherCleanup();
        this.sessionWatcherCleanup = null;
      }
    },

    async fetchUser(): Promise<void> {
      if (!authService.isAuthenticated()) {
        this.user = null;
        localStorage.removeItem('user');
        return;
      }

      try {
        this.user = await authService.getCurrentUser();
        // Persist user to localStorage for page refresh persistence
        localStorage.setItem('user', JSON.stringify(this.user));
      } catch (e) {
        this.user = null;
        localStorage.removeItem('user');
      }
    },

    /**
     * Initialize auth state from localStorage (call on app mount)
     */
    initFromStorage(): void {
      const storedUser = localStorage.getItem('user');
      if (storedUser && authService.isAuthenticated()) {
        try {
          this.user = JSON.parse(storedUser);
        } catch {
          this.user = null;
        }
      }
    },

    hasPermission(permission: string): boolean {
      if (!this.user) return false;
      return (
        this.user.permissions.includes(permission) ||
        this.user.permissions.includes('platform.manage')
      );
    },

    hasAnyPermission(permissions: string[]): boolean {
      return permissions.some((p) => this.hasPermission(p));
    },

    hasAllPermissions(permissions: string[]): boolean {
      return permissions.every((p) => this.hasPermission(p));
    },
  },
});
